import { useState, useMemo } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/core/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { OperatorApp } from '../../features/operator-selection/types/operator-types';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface ActorSelectorProps {
  operators: OperatorApp[];
  selectedOperatorId: string;
  onOperatorChange: (operatorId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export function ActorSelector({
  operators,
  selectedOperatorId,
  onOperatorChange,
  isLoading = false,
  disabled = false,
  className = ""
}: ActorSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  
  const selectedOperator = operators.find(op => op.operatorId.toString() === selectedOperatorId);

  // Smart search with autocomplete functionality
  // Sort operators A-Z by name (per HIVE-6245 requirement #1)
  const filteredOperators = useMemo(() => {
    if (!searchValue.trim()) {
      // When no search, show all operators sorted A-Z by name
      return [...operators].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    }

    const searchLower = searchValue.toLowerCase().trim();

    return operators
      .map((operator: OperatorApp) => {
        // Get operator name and ID
        const operatorName = operator.name || '';
        const operatorId = operator.operatorId.toString();

        // Calculate match score for better sorting
        let score = 0;
        let matchType = '';

        // Exact name match (highest priority)
        if (operatorName.toLowerCase() === searchLower) {
          score = 100;
          matchType = 'exact-name';
        }
        // Name starts with search (high priority)
        else if (operatorName.toLowerCase().startsWith(searchLower)) {
          score = 90;
          matchType = 'starts-with';
        }
        // Name contains search (medium priority)
        else if (operatorName.toLowerCase().includes(searchLower)) {
          score = 70;
          matchType = 'contains';
        }
        // Exact ID match (high priority)
        else if (operatorId === searchValue) {
          score = 85;
          matchType = 'exact-id';
        }
        // ID starts with search (medium priority)
        else if (operatorId.startsWith(searchValue)) {
          score = 60;
          matchType = 'id-starts-with';
        }
        // ID contains search (low priority)
        else if (operatorId.includes(searchValue)) {
          score = 40;
          matchType = 'id-contains';
        }

        return {
          operator,
          score,
          matchType,
          operatorName,
          operatorId
        };
      })
      .filter(result => result.score > 0) // Only include matches
      .sort((a, b) => {
        // Sort by score (highest first)
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // If same score, sort alphabetically by name
        return a.operatorName.localeCompare(b.operatorName);
      })
      .slice(0, 50) // Limit to top 50 results for search
      .map(result => result.operator);
  }, [operators, searchValue]);

  const handleSelect = async (operatorId: string) => {
    onOperatorChange(operatorId);
    setOpen(false);
    setSearchValue('');
  };

  const handleInputChange = (value: string) => {
    setSearchValue(value);
    // Auto-open popover when user starts typing
    if (value && !open) {
      setOpen(true);
    }
  };

  // Read-only display when there is only one operator (distributor / operator user)
  if (!isLoading && operators.length === 1) {
    const op = operators[0];
    return (
      <div className={cn("flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm min-w-0", className)}>
        <span className="font-medium text-gray-900 truncate">{op.name}</span>
        <span className="ml-2 shrink-0 text-xs text-gray-400 font-mono">{op.operatorId}</span>
      </div>
    );
  }

  return (
    <Popover open={open && !disabled} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-48 justify-between", className)}
          onClick={() => {
            if (!disabled) setOpen(!open); // Toggle open state
          }}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading operators...</span>
            </div>
          ) : selectedOperator ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="truncate">{selectedOperator.name}</span>
            </div>
          ) : (
            <span>Select operator...</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Type to search operators..."
            value={searchValue}
            onValueChange={handleInputChange}
            className="border-0 focus:ring-0"
          />
          <CommandList className="max-h-60 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>
              {searchValue ? (
                <div className="text-center py-6">
                  <Search className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">No operators found for "{searchValue}"</p>
                  <p className="text-xs text-gray-400 mt-1">Try searching by name or ID</p>
                </div>
              ) : operators.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">No operators available</p>
                </div>
              ) : null}
            </CommandEmpty>
            <CommandGroup>
              {filteredOperators.map((operator) => {
                const operatorId = operator.operatorId.toString();
                const operatorName = operator.name || '';
                
                return (
                  <CommandItem
                    key={operatorId}
                    value={`${operatorName} ${operatorId}`}
                    onSelect={() => handleSelect(operatorId)}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedOperatorId === operatorId ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {searchValue ? (
                          operatorName.split(new RegExp(`(${searchValue})`, 'gi')).map((part: string, i: number) => 
                            part.toLowerCase() === searchValue.toLowerCase() ? 
                              <mark key={i} className="bg-yellow-200">{part}</mark> : part
                          )
                        ) : (
                          operatorName
                        )}
                      </span>
                      <span className="text-xs text-gray-500">ID: {operatorId}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {filteredOperators.length > 0 && !searchValue && (
              <div className="px-2 py-1.5 text-xs text-gray-500 border-t bg-gray-50">
                <span>Showing all {filteredOperators.length} operators</span>
              </div>
            )}
            {filteredOperators.length > 0 && searchValue && (
              <div className="px-2 py-1.5 text-xs text-gray-500 border-t bg-gray-50">
                <span>Found {filteredOperators.length} matching operators</span>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}